import * as React from "react";
import styled from "styled-components";

import type { AdCopyActivityWithBrand } from "../../../../../../../../../server/models/ad-copy/activity";
import type { AdCopyJob } from "../../../../../../../../../server/models/ad-copy/job";
import type { ErrorObject } from "../../../../../../../../../server/types/error";
import { CurrentProductData } from "../../../../../../../../../server/types/request/report/simulation";
import { AdCopyCPSetting } from "./settings";
import { ChangeRateValue } from "../../../../../../../../../server/models/cp2";
import {
	CurrentProductCorrectionValue,
	SimulationPredictionData,
	SimulationReferenceSourceData,
} from "../../../../../../../../../server/types/request/simulation";
import { AdCopyCP2 } from "../../../../../../../../../server/models/ad-copy/cp2";
import { AdCopyCPParameterSetting } from "./parameter";
import { Constant } from "../../../../../../../../../server/analytics/v3/output";
import { AdCopyTestOnlyConceptKey } from "../../../../../../../../../server/models/ad-copy/concept";

const WrapArea = styled.div`
	margin: 0.8rem;
`;

export type AdCopyCPPageProps = {
	activity: AdCopyActivityWithBrand;
	constValue: Constant;
	currentProduct: CurrentProductData;
	referenceSourceData: SimulationReferenceSourceData;
	data: { [key in AdCopyTestOnlyConceptKey]?: SimulationPredictionData };
	goalRecognitionRate: number;
	errors: ErrorObject | undefined;
	hasEditRole: boolean;
	job: AdCopyJob;
	maxRecognition: number;
	onChange: <K extends keyof CurrentProductCorrectionValue<AdCopyCP2>>(
		key: K,
		value: CurrentProductCorrectionValue<AdCopyCP2>[K],
	) => void;
	onClear: () => void;
	onSubmitPurchaseRate: () => void;
	onSubmitRecognitionRate: () => void;
};

export const AdCopyCPPage = React.memo(
	({
		activity,
		constValue,
		currentProduct,
		errors,
		hasEditRole,
		job,
		goalRecognitionRate,
		referenceSourceData,
		data,
		maxRecognition,
		onChange,
		onClear,
		onSubmitPurchaseRate,
		onSubmitRecognitionRate,
	}: AdCopyCPPageProps) => {
		const { correctionValue, initialValue } = currentProduct;

		const onChangePurchaseRate = React.useCallback(
			(key: keyof ChangeRateValue, value: ChangeRateValue[keyof ChangeRateValue]) => {
				onChange("purchaseRate", { ...(correctionValue.purchaseRate ?? {}), [key]: value } as any);
			},
			[correctionValue, onChange],
		);
		const onChangeRatio = React.useCallback(
			<K extends keyof ChangeRateValue>(name: K, value: ChangeRateValue[K]) => {
				onChange("recognitionRate", { ...(correctionValue.recognitionRate ?? {}), [name]: value } as any);
			},
			[correctionValue, onChange],
		);

		return (
			<WrapArea>
				{/* 現行品/類似品の購入率補整 */}
				<AdCopyCPSetting
					cp={correctionValue}
					errors={errors}
					hasEditRole={hasEditRole}
					job={job}
					onChange={onChangePurchaseRate}
					onClear={onClear}
					onSubmit={onSubmitPurchaseRate}
				/>

				{/* 現行品/類似品の商品認知率補整 */}
				<AdCopyCPParameterSetting
					constValue={constValue}
					cp={correctionValue}
					data={data}
					goalRecognitionRate={goalRecognitionRate}
					referenceSourceData={referenceSourceData}
					initialValue={initialValue}
					hasEditRole={hasEditRole}
					isExistingBrand={activity.isExistingBrand ?? false}
					job={job}
					maxRecognition={maxRecognition}
					onChangeRatio={onChangeRatio}
					onSubmit={onSubmitRecognitionRate}
				/>
			</WrapArea>
		);
	},
);
